import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Loader2, Gift, Copy, CheckCircle, Clock, Sparkles, Users, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { API } from './config';

/**
 * Phase R1 — Creator/User-facing Referral Panel.
 *
 * Shows:
 *   • Personal invite link + copy button
 *   • Current platform commission rate + breakdown
 *   • Referral reward status (active/inactive)
 *   • Per-invitee qualification progress (Stripe + post + meal plan + workout plan)
 *
 * Endpoint contract (Phase R1):
 *   GET /api/referrals/me → {
 *     referral_code, invite_link, invitees[], invited_count,
 *     qualified_count, reward_active, reward_granted_at, i_was_referred
 *   }
 *
 * Does NOT expose any admin notes or other creators' personal data.
 */
export const ReferralPanelModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [commissionRate, setCommissionRate] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    let aborted = false;
    setLoading(true);
    (async () => {
      try {
        const [refRes, connectRes] = await Promise.all([
          axios.get(`${API}/referrals/me`),
          axios.get(`${API}/connect/status`).catch(() => ({ data: null })),
        ]);
        if (aborted) return;
        setData(refRes.data);
        setCommissionRate(connectRes?.data?.commission_rate ?? null);
      } catch (err) {
        if (!aborted) toast.error(err?.response?.data?.detail || 'Could not load your referral info');
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [open]);

  const handleCopy = async () => {
    if (!data?.invite_link) return;
    try {
      await navigator.clipboard.writeText(data.invite_link);
      setCopied(true);
      toast.success('Invite link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error('Could not copy. Long-press the link to copy manually.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="referral-panel-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-amber-500" />
            <span>Invite &amp; Earn</span>
          </DialogTitle>
          <DialogDescription>
            Invite a creator. When they connect Stripe and create one post, one meal plan,
            and one workout plan, your platform commission drops by 5 percentage points
            <strong> permanently</strong>. Founder/Admin approved rates may go as low as 5%.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-amber-500" />
          </div>
        ) : !data ? (
          <div className="py-10 text-center text-sm text-gray-500">Could not load referral info.</div>
        ) : (
          <div className="space-y-5">
            {/* Top-row stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                icon={Sparkles}
                label="Your platform fee"
                value={commissionRate != null ? `${commissionRate}%` : '—'}
                testId="referral-current-rate"
                accent="emerald"
              />
              <StatCard
                icon={Users}
                label="Invited"
                value={data.invited_count}
                testId="referral-invited-count"
              />
              <StatCard
                icon={Trophy}
                label="Qualified"
                value={data.qualified_count}
                testId="referral-qualified-count"
              />
              <StatCard
                icon={Gift}
                label="Reward"
                value={data.reward_active ? 'Active' : 'Pending'}
                testId="referral-reward-status"
                accent={data.reward_active ? 'amber' : 'gray'}
              />
            </div>

            {/* Invite link */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your invite link</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  readOnly
                  value={data.invite_link}
                  className="flex-1 text-sm font-mono"
                  data-testid="referral-invite-link"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  onClick={handleCopy}
                  className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                  data-testid="referral-copy-btn"
                >
                  {copied ? (
                    <><CheckCircle className="h-4 w-4 mr-1.5" /> Copied</>
                  ) : (
                    <><Copy className="h-4 w-4 mr-1.5" /> Copy</>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Your code: <span className="font-mono font-semibold text-gray-700" data-testid="referral-code-value">{data.referral_code}</span>
              </p>
            </div>

            {/* Reward callout */}
            {data.reward_active && (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
                data-testid="referral-reward-active-banner"
              >
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="font-semibold">Your referral reward is active.</p>
                    <p className="text-xs text-emerald-800 mt-1">
                      Your platform commission has been permanently reduced by 5 percentage points.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* R1 polish (2026-06-26): "Your referral checklist" — only renders
                when the current user is themselves an invitee (was referred by
                someone). Reads from `my_invitee_status` on the API response.
                This is for the CURRENT user as an invitee — distinct from the
                Invitees list below which is people the current user invited. */}
            {data.my_invitee_status && (
              <MyInviteeChecklist status={data.my_invitee_status} />
            )}

            {/* Invitee progress list */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Invitees ({data.invitees.length})
              </p>
              {data.invitees.length === 0 ? (
                <div
                  className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500"
                  data-testid="referral-invitees-empty"
                >
                  No invitees yet. Share your link to get started.
                </div>
              ) : (
                <div className="space-y-2" data-testid="referral-invitees-list">
                  {data.invitees.map((inv) => (
                    <InviteeRow key={inv.id} invitee={inv} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


const StatCard = ({ icon: Icon, label, value, testId, accent = 'blue' }) => {
  const accentMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return (
    <div
      className={`rounded-lg border p-3 ${accentMap[accent] || accentMap.blue}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
      </div>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
};


const InviteeRow = ({ invitee }) => {
  const isQualified = !!invitee.qualified_at;
  const progress = invitee.progress || {};
  const gates = [
    { id: 'stripe', label: 'Stripe', ok: !!progress.stripe_connected },
    { id: 'post', label: '1 post', ok: !!progress.has_post },
    { id: 'meal', label: '1 meal plan', ok: !!progress.has_meal_plan },
    { id: 'workout', label: '1 workout', ok: !!progress.has_workout_plan },
  ];
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-3 flex items-center gap-3"
      data-testid={`referral-invitee-${invitee.invitee_id}`}
    >
      <div className="w-9 h-9 rounded-full bg-gray-200 overflow-hidden shrink-0">
        {invitee.invitee_profile_picture ? (
          <img src={invitee.invitee_profile_picture} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-semibold">
            {(invitee.invitee_name || '?').charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{invitee.invitee_name}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {gates.map((g) => (
            <Badge
              key={g.id}
              variant="outline"
              className={`text-[10px] ${g.ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}
              data-testid={`invitee-gate-${invitee.invitee_id}-${g.id}`}
            >
              {g.ok ? <CheckCircle className="h-2.5 w-2.5 mr-0.5" /> : <Clock className="h-2.5 w-2.5 mr-0.5" />}
              {g.label}
            </Badge>
          ))}
        </div>
      </div>
      {isQualified && (
        <Badge className="bg-amber-500 text-white text-[10px] shrink-0" data-testid={`invitee-qualified-${invitee.invitee_id}`}>
          <Trophy className="h-3 w-3 mr-1" /> Qualified
        </Badge>
      )}
    </div>
  );
};



/**
 * R1 polish (2026-06-26): renders the current user's own invitee checklist
 * when they were referred by someone. Mirrors the same 4 gates the backend
 * uses for qualification. Never claims completion unless backend says so.
 */
const MyInviteeChecklist = ({ status }) => {
  const p = status?.progress || {};
  const items = [
    { id: 'stripe', label: 'Connect Stripe as a creator', ok: !!p.stripe_connected },
    { id: 'post', label: 'Create 1 post', ok: !!p.has_post },
    { id: 'meal', label: 'Create 1 meal plan', ok: !!p.has_meal_plan },
    { id: 'workout', label: 'Create 1 workout plan', ok: !!p.has_workout_plan },
  ];
  const allDone = !!status?.all_gates_passed;
  const inviter = status?.inviter_name || 'a OnlyMass member';

  return (
    <div
      className={`rounded-xl border p-4 space-y-3 ${allDone ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
      data-testid="my-invitee-checklist"
    >
      <div className="flex items-start gap-2">
        <Trophy className={`h-4 w-4 mt-0.5 shrink-0 ${allDone ? 'text-emerald-600' : 'text-amber-600'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${allDone ? 'text-emerald-900' : 'text-amber-900'}`}>
            Your referral checklist
          </p>
          <p className={`text-xs mt-0.5 ${allDone ? 'text-emerald-800' : 'text-amber-800'}`}>
            {allDone
              ? <>Completed — {inviter}&apos;s referral reward has been unlocked.</>
              : <>You joined through an invite from <span className="font-medium">{inviter}</span>. Complete these steps to help your inviter unlock their referral reward.</>}
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((it) => (
          <div
            key={it.id}
            className="flex items-center gap-2 text-xs"
            data-testid={`my-invitee-gate-${it.id}`}
          >
            <span
              className={`flex h-4 w-4 items-center justify-center rounded-full shrink-0 ${it.ok ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-300 text-gray-400'}`}
              data-testid={`my-invitee-gate-${it.id}-${it.ok ? 'done' : 'pending'}`}
            >
              {it.ok ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-2.5 w-2.5" />}
            </span>
            <span className={`${it.ok ? 'text-gray-700 line-through decoration-emerald-400 decoration-2' : 'text-gray-700'}`}>
              {it.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
